import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Activity, HelpCircle, Feather, GraduationCap, ShieldCheck, ChevronDown, ChevronUp } from 'lucide-react';
import usePriceStream from '@/hooks/usePriceStream';
import './landing.css';
import SiteDisclaimer from '@/components/SiteDisclaimer';
import LandingStatsStrip from '@/components/LandingStatsStrip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Round 91j — Market session helpers.
// DiviDrip uses an extended Eastern-Time session window of
// 4:00 AM → 8:00 PM ET on weekdays (matches Stock Modal "Today" tab).
// Returns { phase, msUntilOpen, et }. Uses Intl.DateTimeFormat with
// `timeZone: 'America/New_York'` so DST is handled by the runtime.
function getEasternParts(date) {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    weekday: 'short',
  });
  const parts = Object.fromEntries(fmt.formatToParts(date).map((p) => [p.type, p.value]));
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour === '24' ? '0' : parts.hour),
    minute: Number(parts.minute),
    second: Number(parts.second),
    weekday: parts.weekday, // 'Mon' | ... | 'Sun'
  };
}
const OPEN_HOUR = 4;   // 4:00 AM ET
const CLOSE_HOUR = 20; // 8:00 PM ET

function getMarketSession(now = new Date(), holidayList = null) {
  // Round 91s — accept either a Set<string> of dates OR an array of
  // { date, name, status } so we can surface the holiday name in the UI.
  // Normalise both shapes once.
  let holidaySet = null;
  let holidayNameMap = null;
  if (holidayList instanceof Set) {
    holidaySet = holidayList;
  } else if (Array.isArray(holidayList)) {
    holidaySet = new Set();
    holidayNameMap = new Map();
    for (const h of holidayList) {
      if (h && h.date && h.status === 'closed') {
        holidaySet.add(h.date);
        if (h.name) holidayNameMap.set(h.date, h.name);
      }
    }
  }
  const et = getEasternParts(now);
  const isWeekend = ['Sat', 'Sun'].includes(et.weekday);
  const minutesNow = et.hour * 60 + et.minute;
  const openMin = OPEN_HOUR * 60;
  const closeMin = CLOSE_HOUR * 60;

  const todayKey = `${et.year}-${String(et.month).padStart(2, '0')}-${String(et.day).padStart(2, '0')}`;
  const isHolidayToday = !!(holidaySet && holidaySet.has(todayKey));

  if (!isWeekend && !isHolidayToday && minutesNow >= openMin && minutesNow < closeMin) {
    return { phase: 'open', msUntilOpen: 0, et, holidayName: null };
  }

  let deltaMinutes;
  let skippedHolidayName = null;
  if (!isWeekend && !isHolidayToday && minutesNow < openMin) {
    deltaMinutes = openMin - minutesNow;
  } else {
    const minutesLeftToday = 24 * 60 - minutesNow;
    let skipFullDays = 0;
    let probeDate = new Date(now.getTime() + (24 * 60 * 60 * 1000));
    while (true) {
      const probeEt = getEasternParts(probeDate);
      const probeKey = `${probeEt.year}-${String(probeEt.month).padStart(2, '0')}-${String(probeEt.day).padStart(2, '0')}`;
      const probeIsWeekend = ['Sat', 'Sun'].includes(probeEt.weekday);
      const probeIsHoliday = !!(holidaySet && holidaySet.has(probeKey));
      if (!probeIsWeekend && !probeIsHoliday) break;
      if (probeIsHoliday && holidayNameMap && !skippedHolidayName) {
        skippedHolidayName = holidayNameMap.get(probeKey) || null;
      }
      skipFullDays += 1;
      probeDate = new Date(probeDate.getTime() + (24 * 60 * 60 * 1000));
      if (skipFullDays > 14) break;
    }
    deltaMinutes = minutesLeftToday + skipFullDays * 24 * 60 + openMin;
  }
  const deltaMs = deltaMinutes * 60 * 1000 - et.second * 1000;
  const holidayName = isHolidayToday
    ? (holidayNameMap ? holidayNameMap.get(todayKey) || null : null)
    : skippedHolidayName;
  return {
    phase: isHolidayToday ? 'closed-holiday' : (isWeekend ? 'closed-weekend' : 'closed-weekday'),
    msUntilOpen: Math.max(0, deltaMs),
    et,
    holidayName,
  };
}

function formatCountdown(ms) {
  if (!ms || ms < 0) return '00h 00m 00s';
  const total = Math.floor(ms / 1000);
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (d > 0) return `${d}d ${String(h).padStart(2, '0')}h ${String(m).padStart(2, '0')}m`;
  return `${String(h).padStart(2, '0')}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`;
}

/**
 * Concept 1 — TERMINAL (Round 90c, refined per user feedback).
 *
 * Calendar shows ONLY Mon/Wed/Fri (3 cards). Each card carries live
 * websocket-streamed prices. Today's card gets a ✦ pulse so the page
 * feels alive on visit.
 *
 * Removed: flip-gradient toggle (user just wanted to preview), arrow
 * icons on buttons.
 */
const TAPE = [
  // Round 91i — Expanded to 36 unique tickers (was 12). Mix of
  // dividend kings/aristocrats, monthly REITs, high-yield CEFs/ETFs,
  // and the YieldMax / covered-call ETFs people search for here.
  // Rendered TWICE in the track with a -50% animation translate so
  // the loop is end-to-end seamless (no visible gap on the wrap).
  { t: 'JEPI', d: 'up',   v: '+0.42%' }, { t: 'SCHD', d: 'up',   v: '+0.18%' },
  { t: 'JEPQ', d: 'down', v: '-0.15%' }, { t: 'VYM',  d: 'up',   v: '+0.31%' },
  { t: 'O',    d: 'up',   v: '+0.66%' }, { t: 'MAIN', d: 'up',   v: '+0.51%' },
  { t: 'JNJ',  d: 'up',   v: '+0.27%' }, { t: 'PG',   d: 'up',   v: '+0.13%' },
  { t: 'KO',   d: 'up',   v: '+0.22%' }, { t: 'PEP',  d: 'down', v: '-0.18%' },
  { t: 'MO',   d: 'up',   v: '+0.74%' }, { t: 'XOM',  d: 'up',   v: '+0.91%' },
  { t: 'CVX',  d: 'down', v: '-0.46%' }, { t: 'VZ',   d: 'up',   v: '+0.55%' },
  { t: 'T',    d: 'up',   v: '+0.31%' }, { t: 'IBM',  d: 'down', v: '-0.62%' },
  { t: 'MMM',  d: 'down', v: '-0.34%' }, { t: 'CAT',  d: 'up',   v: '+0.88%' },
  { t: 'MCD',  d: 'up',   v: '+0.45%' }, { t: 'HD',   d: 'down', v: '-0.27%' },
  { t: 'LOW',  d: 'up',   v: '+0.39%' }, { t: 'WMT',  d: 'up',   v: '+0.19%' },
  { t: 'TGT',  d: 'down', v: '-0.81%' }, { t: 'COST', d: 'up',   v: '+1.04%' },
  { t: 'ABBV', d: 'up',   v: '+0.62%' }, { t: 'LLY',  d: 'up',   v: '+1.41%' },
  { t: 'PFE',  d: 'down', v: '-0.74%' }, { t: 'BTI',  d: 'up',   v: '+0.88%' },
  { t: 'STAG', d: 'up',   v: '+0.36%' }, { t: 'STWD', d: 'up',   v: '+1.12%' },
  { t: 'AGNC', d: 'down', v: '-0.45%' }, { t: 'EPR',  d: 'up',   v: '+0.78%' },
  { t: 'DIVO', d: 'up',   v: '+0.21%' }, { t: 'SPYI', d: 'up',   v: '+0.18%' },
  { t: 'XYLD', d: 'down', v: '-0.08%' }, { t: 'QYLD', d: 'down', v: '-0.12%' },
  { t: 'ORC',  d: 'up',   v: '+1.84%' }, { t: 'TRIN', d: 'up',   v: '+0.97%' },
];

// YYYY-MM-DD in New York time — pay-day cards compare against the market's calendar, not the visitor's.
const TODAY_ET_ISO = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
function addDaysIso(iso, n) {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

// Round 90d — Tiny inline SVG sparkline rendered under each card's
// Current price. Pure presentation, takes the last N monthly prices,
// draws a 1px polyline. Auto-tones up/down based on first→last delta:
//   - emerald if the ticker is higher than 12 months ago
//   - rose if lower
//   - violet (neutral) if essentially flat
// Width is fluid via 100% so it fills the card slot at any breakpoint.
function Sparkline({ points }) {
  if (!points || points.length < 2) return null;
  const w = 100;
  const h = 18;
  const lo = Math.min(...points);
  const hi = Math.max(...points);
  const range = hi - lo || 1;
  const step = w / (points.length - 1);
  const path = points
    .map((p, i) => {
      const x = i * step;
      const y = h - ((p - lo) / range) * h;
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');
  const delta = points[points.length - 1] - points[0];
  const pctDelta = points[0] ? delta / points[0] : 0;
  const stroke =
    pctDelta > 0.005 ? '#34d399' : pctDelta < -0.005 ? '#fb7185' : '#a78bfa';
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      width="100%"
      height={h}
      preserveAspectRatio="none"
      aria-hidden="true"
      data-testid="lp1-sparkline"
    >
      <path d={path} fill="none" stroke={stroke} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function Landing1() {
  const [week, setWeek] = useState(null);
  const [sparks, setSparks] = useState({}); // ticker → [12 last monthly prices]
  // Phone-only collapse of the two long feature lists.
  const [isPhone, setIsPhone] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = (e) => setIsPhone(e.matches);
    setIsPhone(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  const [noAcctOpen, setNoAcctOpen] = useState(false);
  const [freeAcctOpen, setFreeAcctOpen] = useState(false);
  const noAcctCollapsed = isPhone && !noAcctOpen;
  const freeAcctCollapsed = isPhone && !freeAcctOpen;


  // Round 90 — apply landing theme to the global AppHeader on mount.
  // Round 91 — the TwylightCrow landing is intentionally a dark-only
  // brand page. If the user has Light theme active globally, the
  // page-scoped CSS still paints the header dark while the global
  // `.dark` class is absent, which makes header buttons render dark
  // text on dark bg (invisible). To keep the brand intact AND keep
  // navigation legible regardless of preference, we force `dark` on
  // <html> while mounted and restore the previous state on unmount,
  // so leaving the page returns the user to their saved preference.
  useEffect(() => {
    document.body.classList.add('lp1-active');
    const root = document.documentElement;
    const wasDark = root.classList.contains('dark');
    root.classList.add('dark');
    return () => {
      document.body.classList.remove('lp1-active');
      if (!wasDark) root.classList.remove('dark');
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/landing/week-of-payouts`)
      .then((r) => { if (!cancelled) setWeek(r.data); })
      .catch(() => { if (!cancelled) setWeek({ days: [] }); });
    return () => { cancelled = true; };
  }, []);

  // Filter to Mon/Wed/Fri only (user spec — most active payout days).
  const mwfDays = useMemo(() => {
    const days = week?.days || [];
    return days.filter((d) => ['Mon', 'Wed', 'Fri'].includes(d?.weekday));
  }, [week]);

  // Stable ticker array memoized so usePriceStream doesn't re-subscribe
  // on every render (the hook's stability requirement).
  // Round 91j — fold the 36 tape tickers into the same subscription so
  // we get ONE SSE connection feeding both the MWF cards AND the live
  // tape at the top of the page (cheap & avoids two parallel streams).
  const tickerList = useMemo(() => {
    const mwf = mwfDays.map((d) => d.ticker).filter(Boolean);
    const tapeTickers = TAPE.map((t) => t.t);
    return Array.from(new Set([...mwf, ...tapeTickers]));
  }, [mwfDays]);
  const { prices, status } = usePriceStream(tickerList);

  // Round 91s — fetch upcoming market holidays so the session indicator
  // skips full-day closures (e.g. Memorial Day) AND surfaces the holiday
  // name in the UI. Store the full list (date+name+status) — the helper
  // normalises it into a Set + name-map internally.
  const [holidays, setHolidays] = useState(null);
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/landing/market-holidays`)
      .then((r) => {
        if (cancelled) return;
        const list = Array.isArray(r.data?.holidays) ? r.data.holidays : [];
        setHolidays(list);
      })
      .catch(() => { if (!cancelled) setHolidays([]); });
    return () => { cancelled = true; };
  }, []);

  // Round 91j — Market session indicator: re-evaluates every second
  // so the countdown ticks live. When the market is open we don't
  // need the per-second refresh, but the cost is negligible and the
  // single source of truth keeps phase + countdown consistent.
  const [session, setSession] = useState(() => getMarketSession());
  useEffect(() => {
    const tick = () => setSession(getMarketSession(new Date(), holidays));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [holidays]);

  // Round 90d — fetch yield-history for each card ticker so we can
  // render a "last 12 months → today" mini sparkline. The endpoint
  // returns monthly_data which is what the Compare modal already
  // consumes; we just slice the last 12 price points. One fetch per
  // ticker, in parallel.
  // Round 91j — only fetch for the MWF card tickers, NOT every ticker
  // in the SSE subscription (the 36 tape tickers don't need sparklines).
  const cardTickers = useMemo(
    () => mwfDays.map((d) => d.ticker).filter(Boolean),
    [mwfDays],
  );
  useEffect(() => {
    if (cardTickers.length === 0) return;
    let cancelled = false;
    Promise.all(
      cardTickers.map((t) =>
        axios
          .get(`${API}/stocks/${t}/yield-history`)
          .then((r) => [t, (r.data?.monthly_data || []).slice(-12).map((p) => p.price).filter((v) => v != null)])
          .catch(() => [t, []]),
      ),
    ).then((pairs) => {
      if (cancelled) return;
      setSparks(Object.fromEntries(pairs));
    });
    return () => { cancelled = true; };
  }, [cardTickers]);

  return (
    <div className="lp1 lp-shell" data-testid="landing-1">
      {/* LandingSwitcher removed Round 91l — Landing1 is now the
          canonical start page; concepts 2–5 stay routable for
          archival reference but the 1/2/3/4/5 picker no longer
          ships on the public start page. */}

      {/* Live ticker tape — Round 91i: two seamless copies so the
          translate -50% loop wraps end-to-end with no visible gap.
          Round 91j: each item pulls its live `change` % out of the
          shared usePriceStream `prices` map.
          Round 91k: when the market is closed the whole tape slows
          to half-speed and dims slightly so the page chrome reflects
          the closed-market state. */}
      <div className={`tape ${session.phase !== 'open' ? 'tape-closed' : ''}`}>
        <div className="tape-track">
          {[...TAPE, ...TAPE].map((t, i) => {
            const live = prices?.[t.t];
            const liveChange = typeof live?.change === 'number' ? live.change : null;
            const dir = liveChange != null
              ? (liveChange >= 0 ? 'up' : 'down')
              : t.d;
            const display = liveChange != null
              ? `${liveChange >= 0 ? '+' : ''}${liveChange.toFixed(2)}%`
              : t.v;
            return (
              <span key={i} className="tape-item">
                {t.t} <span className={dir}>{display}</span>
              </span>
            );
          })}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 pt-10 pb-0">
        {/* Round 91j — Dynamic market session indicator.
            Open  → green dot + [ SESSION ▌ MARKET OPEN ]
            Closed weekday/weekend → amber dot + [ MARKET CLOSED ▌ OPENS IN 02h 14m 33s ]
            Closed with holiday    → "MARKET CLOSED - Memorial Day" + countdown.
            Round 91s tweak: phone view stacks status on line 1 and the
            countdown on line 2 (no brackets to save horizontal width);
            sm: and up keeps the bracketed single-line terminal style. */}
        <div
          className={`text-[10px] sm:text-xs tracking-[0.22em] sm:tracking-[0.35em] mb-2 flex flex-col sm:flex-row sm:items-center sm:flex-wrap gap-x-2 gap-y-1 ${
            session.phase === 'open' ? 'text-cyan-400/80' : 'text-amber-300/85'
          }`}
          data-testid="lp1-session-indicator"
        >
          {session.phase === 'open' ? (
            <span className="inline-flex items-center gap-2">
              <span
                className={`inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.85)] animate-pulse`}
                aria-hidden
              />
              <span className="sm:hidden">MARKET OPEN</span>
              <span className="hidden sm:inline">[ SESSION ▌ MARKET OPEN ]</span>
            </span>
          ) : (
            <>
              {/* Line 1 — status + holiday name (phone & desktop) */}
              <span className="inline-flex items-center gap-2">
                <span
                  className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.7)]"
                  aria-hidden
                />
                <span className="sm:hidden">
                  MARKET CLOSED
                  {session.holidayName ? <> - {session.holidayName}</> : null}
                </span>
                <span className="hidden sm:inline">
                  [ MARKET CLOSED
                  {session.holidayName ? <> ▌ {session.holidayName.toUpperCase()}</> : null}
                  {' '}▌ OPENS IN {formatCountdown(session.msUntilOpen)} ET ]
                </span>
              </span>
              {/* Line 2 — countdown (phone only; desktop keeps it inline above) */}
              <span className="sm:hidden pl-3.5" data-testid="lp1-session-closed-countdown">
                OPENS IN {formatCountdown(session.msUntilOpen)} ET
              </span>
            </>
          )}
        </div>

        {/* Big logo wordmark — single direction (light → dark).
            Round 91v — the tagline below is sized + justified so its
            full text spans EXACTLY the rendered width of DIVIDRIP,
            never overflowing it. `inline-block` container locks width
            to the title; `text-align-last: justify` on the tagline
            distributes word spacing to fill that width. */}
        <div className="inline-block" data-testid="lp1-title-block">
          <h1 className="ascii-title-tc" data-testid="lp1-title">
            DIVIDRIP
          </h1>
          <p className="ascii-tagline-tc" data-testid="lp1-tagline">
            Dividend Stock and ETF Portfolio Tracker
          </p>
        </div>

        <p className="mt-6 text-base sm:text-lg text-violet-100/75 max-w-2xl leading-relaxed">
          Dividend info and stock metrics — <span className="text-yellow-300 font-bold">always free.</span>{' '}
          <span className="text-violet-100/55">Create a free account to build a portfolio and watchlist.</span>
        </p>
        <LandingStatsStrip />

        {/* Primary nav links — text + icon style matching the app
            header. Dashboard keeps its old button color as text with
            the Activity icon; Privacy uses the old Glossary-button
            fuchsia. */}
        <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-3">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-1.5 font-semibold hover:underline underline-offset-2"
            data-testid="lp1-cta-dashboard"
            title="Open the DiviDrip dashboard"
          >
            <Activity className="w-5 h-5 text-cyan-400/75 shrink-0" strokeWidth={2.2} />
            <span className="text-[15px] sm:text-[17px] text-fuchsia-700">Dashboard</span>
          </Link>
          <Link
            to="/how-to"
            className="inline-flex items-center gap-1.5 font-semibold hover:underline underline-offset-2"
            data-testid="lp1-cta-howto"
            title="Open the DiviDrip user guide"
          >
            <HelpCircle className="w-5 h-5 text-amber-500 shrink-0" strokeWidth={2.4} />
            <span className="text-sm sm:text-base text-amber-400">How To</span>
          </Link>
          <Link
            to="/glossary"
            className="inline-flex items-center gap-1.5 font-semibold hover:underline underline-offset-2"
            data-testid="lp1-cta-glossary"
            title="Plain-English definitions of every dividend, tax, and metrics term used across DiviDrip"
          >
            <Feather className="w-5 h-5 text-purple-200 shrink-0" strokeWidth={2.2} />
            <span className="text-sm sm:text-base text-purple-200">Glossary</span>
          </Link>
          <Link
            to="/learn"
            className="inline-flex items-center gap-1.5 font-semibold hover:underline underline-offset-2"
            data-testid="lp1-cta-learn"
            title="Free dividend investing guides"
          >
            <GraduationCap className="w-5 h-5 text-violet-400 shrink-0" strokeWidth={2.2} />
            <span className="text-sm sm:text-base text-violet-300">Learn</span>
          </Link>
          <Link
            to="/privacy-policy"
            className="inline-flex items-center gap-1.5 font-semibold hover:underline underline-offset-2"
            data-testid="lp1-cta-privacy"
            title="DiviDrip privacy policy"
          >
            <ShieldCheck className="w-5 h-5 text-[#e879f9] shrink-0" strokeWidth={2.2} />
            <span className="text-sm sm:text-base text-[#e879f9]">Privacy Policy</span>
          </Link>
        </div>

        {/* Mon/Wed/Fri calendar — live prices, today pulses */}
        <div className="mt-[46px]">
          <div className="text-base sm:text-lg text-violet-100/85 max-w-2xl mb-4 leading-relaxed">
            Live exchange data shows you what&apos;s landing this week.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3" data-testid="lp1-week-grid">
            {(mwfDays.length ? mwfDays : Array(3).fill({})).map((d, i) => {
              const offset = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4 }[d?.weekday];
              const cardDate = week?.week_of && offset != null ? addDaysIso(week.week_of, offset) : null;
              const payStatus = cardDate ? (cardDate === TODAY_ET_ISO ? 'today' : cardDate < TODAY_ET_ISO ? 'paid' : 'upcoming') : null;
              const isToday = payStatus === 'today';
              const live = d?.ticker ? prices?.[d.ticker.toUpperCase()] : null;
              const livePrice = live?.price;
              const liveChange = live?.change;
              // Clickable when a ticker is present — jumps to the
              // dashboard and auto-opens the Stock Modal via ?stock=.
              const CellTag = d?.ticker ? Link : 'div';
              const cellProps = d?.ticker
                ? { to: `/dashboard?stock=${d.ticker}`, title: `Open ${d.ticker} in the Stock Modal` }
                : {};
              return (
                <CellTag
                  key={i}
                  {...cellProps}
                  className={`week-cell block ${isToday ? 'is-today' : ''} ${d?.ticker ? 'cursor-pointer hover:border-cyan-400/50 transition-colors' : ''}`}
                  data-testid={`lp1-week-cell-${i}`}
                >
                  {isToday && (
                    <span className="today-pulse" aria-hidden="true" data-testid="lp1-today-pulse">✦ TODAY</span>
                  )}
                  {payStatus === 'upcoming' && (
                    <span className="week-status text-cyan-300" data-testid={`lp1-week-status-${i}`}>UPCOMING</span>
                  )}
                  {payStatus === 'paid' && (
                    <span className="week-status text-emerald-300/80" data-testid={`lp1-week-status-${i}`}>$ PAID</span>
                  )}
                  <div className="text-[10px] sm:text-xs text-violet-300/70 font-mono">{d.month_day || '—'}</div>
                  <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-violet-300/85 font-bold mt-0.5">{d.weekday || '—'}</div>
                  {d?.ticker ? (
                    <>
                      <div className="text-base sm:text-lg font-black text-yellow-300 font-mono mt-2">{d.ticker}</div>
                      <div className="text-[9px] sm:text-[10px] text-violet-200/60 truncate leading-tight" title={d.name}>{d.name || ''}</div>
                      {/* Live price block — switches to ws value when stream lands */}
                      <div className="mt-2 grid grid-cols-2 gap-2 pt-2 border-t border-violet-400/15">
                        <div>
                          <div className="text-[9px] text-violet-300/55">Current</div>
                          <div className="text-sm font-bold text-violet-50 font-mono live-flash" data-testid={`lp1-price-${d.ticker}`}>
                            {livePrice != null ? `$${Number(livePrice).toFixed(2)}` : '—'}
                          </div>
                          {liveChange != null && (
                            <div className={`text-[9px] font-mono ${liveChange >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
                              {liveChange >= 0 ? '+' : ''}{Number(liveChange).toFixed(2)}
                            </div>
                          )}
                          {/* Round 90d — 12-mo price sparkline */}
                          <div className="mt-1 opacity-85">
                            <Sparkline points={sparks[d.ticker]} />
                          </div>
                        </div>
                        <div>
                          <div className="text-[9px] text-violet-300/55">{payStatus === 'paid' ? 'Paid per share' : 'Per share'}</div>
                          <div className="text-sm font-bold text-yellow-300 font-mono">${(d.amount || 0).toFixed(2)}</div>
                          {d.yield_pct != null && (
                            <div className="text-[9px] text-cyan-300/80 font-mono">Yld {Number(d.yield_pct).toFixed(2)}%</div>
                          )}
                          {payStatus === 'paid' && d.amount > 0 && (
                            <div className="text-[9px] text-emerald-300/85 font-mono mt-0.5" data-testid={`lp1-paid-recap-${i}`}>
                              ${(d.amount * 100).toFixed(2)} per 100 sh
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-[10px] text-violet-300/30 italic mt-4">No popular payouts</div>
                  )}
                </CellTag>
              );
            })}
          </div>
          <div className="text-[10px] text-violet-300/45 font-mono mt-3 flex items-center gap-2">
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${status === 'connected' ? 'bg-emerald-400 animate-pulse' : status === 'dormant' ? 'bg-amber-300' : 'bg-violet-400/40'}`} />
            {status === 'connected' ? 'Live · WebSocket' : status === 'dormant' ? 'Static (WS offline)' : 'Connecting…'}
            <span className="opacity-50">· Week of {week?.week_of || '…'}</span>
          </div>
        </div>

        {/* Features — two-column split */}
        <div className="mt-10" data-testid="lp1-features">
          <div className="text-cyan-400/80 text-xs tracking-[0.3em] mb-6">[ FEATURES ]</div>
          <div className="grid md:grid-cols-2 gap-3">
            <div className="feat-card">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-[10px] uppercase tracking-[0.18em] text-emerald-300/80 font-bold">No Account Needed</span>
                <span className="text-[9px] text-violet-300/40 font-mono">— browse anonymously</span>
              </div>
              <ul className="space-y-1.5 text-[13px] text-violet-100/85">
                {[
                  'Browse 5,000+ dividend-paying tickers (Screener + full Stock Modal on every one)',
                  'Mega Tier Undervalued Stocks — weekly 3-stage value funnel (combined rank → Graham → Piotroski F-Score) with per-check breakdown modals',
                  'Market Movers News — merged Massive + Yahoo headlines on the day\u2019s top gainers and losers, with sentiment',
                  'Research hubs: Dividends by Sector (14 sectors + 14 industries), Foreign & ADR Payers with treaty withholding calculator, Canadian Payers (CAD→USD corrected), Money Market Yields (SEC N-MFP data), Covered Call ETF list with true total return',
                  'Analyst Coverage card — monthly Strong Buy → Strong Sell consensus, price targets, Upgrading / Downgrading and Thin-coverage badges',
                  'Special-dividend detection — one-off payouts are never counted as yield; “Pays specials” tag + screener filter for habitual special payers',
                  'Forward dividend rate — a declared cut or raise shows up before it is paid',
                  'Valuation Multiples vs sector median with cheaper / richer explainer cards and 5-cheapest-peers drill-down',
                  'Public DRIP Calculator, “Is X a dividend stock?” and ex-dividend-date pages for every ticker',
                  'Stock cards: price, yield, ex-div, pay date',
                  'Financial Health tab — live ratios + income / cash flow / balance sheet, Dividend FCF Coverage (Massive)',
                  'Asset Class detection — baby bonds, preferred shares, preferred units, mortgage bonds get correct par × coupon math',
                  'Historical dividend bar charts (3-year)',
                  'Yield history line charts',
                  'Dividend growth (1y/3y/5y CAGR)',
                  'Dividend Quality Score (0–100) on every ticker — built from SEC filing data, filterable in the Screener',
                  'Dividend FCF Coverage column + Coverage Watch / Recovery Radar screener presets',
                  'Tier badges: Kings · Aristocrats · Achievers',
                  'Classification chips: ETF · CEF · BDC · REIT · ADR',
                  'Recent hike/cut alerts (last 90 days)',
                  'Macro dashboard — Treasury yields, CPI, unemployment + a history-matched regime playbook',
                  'Squeeze Radar — short-interest squeeze candidates',
                  'Streaks Extended & Streaks At Risk radars',
                  'Top Movers (gainers / losers, sortable)',
                  'Real vs Nominal yield toggle (CPI-adjusted)',
                  'Founder-Led leaderboard — insider skin-in-the-game from SEC Form 3 filings',
                  'IPO Watch — new REIT / CEF / BDC / MLP / Preferred listings + SPAC froth gauge',
                  'Intraday "Today" chart with pre-market prints',
                  'Compare any two tickers (yield, CAGR, quality)',
                  'Triangle Compare — up to 4 tickers (Revenue · EPS · Dividend · Score · Flag)',
                  'Dividend Triangle radar for any ticker',
                  'Stock Screener with 30+ filter criteria',
                  'Challenge pages — Kings · Aristocrats · Achievers leaderboards',
                  'Valuation Matrix — model-based target buy price on every stock',
                  'Live WebSocket price ticks',
                  'Badge Key — decode every row badge',
                  'Non-dividend growth research — Minervini SEPA Lite, Capital Analytics (Rule of 40, Altman, Beneish), Stage 2 Leaders — coming to DiviDrip Plus',
                  'Glossary (300+ terms), How To, 80+ Learn guides for dividend AND non-dividend investors',
                ].filter((_, i) => (noAcctCollapsed ? i < 7 : true)).map((s) => (
                  <li key={s} className="flex items-start gap-2">
                    <span className="text-emerald-400/80 mt-1 shrink-0">▸</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
              {isPhone && (
                <button
                  type="button"
                  onClick={() => setNoAcctOpen((v) => !v)}
                  data-testid="lp1-no-account-toggle"
                  className="mt-3 w-full flex items-center justify-center gap-1.5 text-[12px] font-mono tracking-wide text-emerald-300/90 py-2 rounded-md border border-emerald-400/25 bg-emerald-400/5 active:bg-emerald-400/10 transition-colors"
                >
                  {noAcctOpen ? 'View Less' : 'View More'}
                  {noAcctOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>
              )}
            </div>

            <div className="feat-card feat-card-yellow">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-[10px] uppercase tracking-[0.18em] text-yellow-300 font-bold">With Free Account</span>
                <span className="text-[9px] text-violet-300/40 font-mono">— sign in with Google</span>
              </div>
              <ul className="space-y-1.5 text-[13px] text-violet-100/85">
                {[
                  'Everything in No Account Needed Plus+',
                  'Build your portfolio with multi-lot tracking (Buy / DRIP / Sell)',
                  'Hollow → solid water-drop toggle to flip any lot to a DRIP buy',
                  'Capital Tracker: Add / Withdraw Outside or Inside cash + Remove typo entries',
                  'Live portfolio value, gain/loss, yield-on-cost',
                  'Personal dividend calendar (your tickers only)',
                  'Portfolio dividend payout reminders',
                  'Multiple named watchlists (★ star on any row)',
                  'Watchlist Radar: ex-div countdown, 52w levels, income impact, 5Y YoC',
                  'Custom pop-up alerts — ex-div reminders & new-IPO alerts in the top-nav bell',
                  'Sector rebalancer + peer suggestions',
                  'Benchmark Comparison vs SCHD / VYM / SPY',
                  'Pinned Compare pairs (up to 15, each with a 140-char memo)',
                  'Stock Modal DRIP Calc (5y / 10y / 20y projections) with tax-rate input',
                  'Portfolio DRIP Calculator — type any positive number of years',
                  'What If? — historical $X buy projection',
                  'Tax-lot harvest report (educational view)',
                  'Year-End summary for tax season',
                  'Account Audit — reconcile your broker statement against DiviDrip',
                  'Google / Excel Sheet portfolio export',
                  'My Share Page — shareable public link to your portfolio',
                  'Streak tracking + dividend journal (Thesis on Add/Sell, Cuts, Reviews)',
                  'Conviction vs Outcome chart (unlocks at 20 entries)',
                  'Executive Summary — Bull / Bear thesis starter on any stock, plus Compare Theses side-by-side',
                  'Ex-div, filing, hike / cut and Stage 2 / SEPA bell alerts for your watchlist',
                  'Watchlist stars on Mega Tier, Market Movers News and the Screener',
                  'Time Machine — what your watchlist would have done over the last year vs SPY / SCHD / VYM',
                  'Coverage Radar cards and Watchlist Snapshot (average conviction, compare quality / theses)',
                  'Was-this-helpful feedback on every How To section and Learn guide',
                ].filter((_, i) => (freeAcctCollapsed ? i < 10 : true)).map((s) => (
                  <li key={s} className="flex items-start gap-2">
                    <span className="text-yellow-300/90 mt-1 shrink-0">▸</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
              {isPhone && (
                <button
                  type="button"
                  onClick={() => setFreeAcctOpen((v) => !v)}
                  data-testid="lp1-free-account-toggle"
                  className="mt-3 w-full flex items-center justify-center gap-1.5 text-[12px] font-mono tracking-wide text-yellow-300/90 py-2 rounded-md border border-yellow-300/25 bg-yellow-300/5 active:bg-yellow-300/10 transition-colors"
                >
                  {freeAcctOpen ? 'View Less' : 'View More'}
                  {freeAcctOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="mt-5 flex items-center justify-center text-center gap-2 text-violet-300/65 text-[10px] tracking-[0.3em]">
          <Activity className="w-4 h-4 flex-shrink-0 text-cyan-400/75" />
          <span>[ DIVIDRIP // BUILT BY A DIVIDEND INVESTOR, FOR DIVIDEND INVESTORS // 2026 ]</span>
        </div>
      </div>
      <SiteDisclaimer linksOnly />
    </div>
  );
}
